import { useRef, useState, type ReactNode } from 'react';
import {
  Button,
  Popover,
  Tooltip,
  TooltipTrigger,
  type Placement,
} from 'react-aria-components';
import { css } from '@understory-io/leaf/css';
import { InfoIcon } from '~/ui/icons/info';

type InfoPopoverProps = {
  /** Accessible name for the trigger — the icon carries no text on its own. */
  label: string;
  /** Content shown in both the hover tooltip and the click popover. Pass
   *  already-styled, non-interactive content (e.g. a Leaf `<Text>`). */
  children: ReactNode;
  /** Preferred placement relative to the icon. Defaults to `top`; react-aria
   *  flips it automatically when there isn't room. */
  placement?: Placement;
  /** Show the content on pointer hover / keyboard focus via a tooltip, in
   *  addition to the always-on click/tap popover. react-aria never shows
   *  tooltips on touch, so touch users always get the popover. Default `true`. */
  openOnHover?: boolean;
};

/**
 * An "ⓘ" icon that reveals supplementary content two ways:
 *
 * - **Hover / focus** → a react-aria `Tooltip` (it owns all the warmup, focus,
 *   and hide-on-press timing, so there's no hand-rolled hover state to glitch).
 * - **Click / tap** → a persistent `Popover` we control with `isOpen`.
 *
 * The tooltip is disabled while the popover is open, so the two never overlap,
 * and pressing dismisses the tooltip before opening the popover. This matches
 * the legacy storefront's hover-and-tap feel while staying accessible across
 * mouse, keyboard, and touch (react-aria suppresses tooltips on touch, so touch
 * users get the popover).
 *
 * Content is supplementary by design: keep it short and non-interactive.
 */
export function InfoPopover({
  label,
  children,
  placement = 'top',
  openOnHover = true,
}: InfoPopoverProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <TooltipTrigger isDisabled={!openOnHover || isOpen} delay={300}>
        <Button
          ref={triggerRef}
          aria-label={label}
          aria-expanded={isOpen}
          className={triggerStyle}
          onPress={() => setIsOpen((open) => !open)}
        >
          <InfoIcon />
        </Button>
        <Tooltip placement={placement} className={tooltipStyle}>
          {children}
        </Tooltip>
      </TooltipTrigger>
      <Popover
        // The popover surface renders with role="dialog"; give it an
        // accessible name (reusing the trigger label) so it isn't an unnamed
        // dialog — axe flags `aria-dialog-name` otherwise.
        aria-label={label}
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        placement={placement}
        className={popoverStyle}
      >
        <div className={contentStyle}>{children}</div>
      </Popover>
    </>
  );
}

const triggerStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'content.tertiary',
  cursor: 'interactive',
  borderRadius: 'small',
  outline: 'none',
  '&[data-hovered]': { color: 'content.secondary' },
  '&[data-focus-visible]': {
    outlineWidth: '[2px]',
    outlineStyle: 'solid',
    outlineColor: 'border.active',
    outlineOffset: '[2px]',
  },
});

const surface = {
  textStyle: 'body-small-regular',
  background: 'content.secondary',
  color: 'content.contrast',
  maxWidth: '[280px]',
  borderRadius: 'small',
  borderStyle: 'solid',
  boxShadow: 'medium',
  outline: 'none',
} as const;

const tooltipStyle = css({ ...surface, padding: 'small' });

const popoverStyle = css(surface);

const contentStyle = css({ padding: 'small', outline: 'none' });
